import AffiliateSignupModal from "@components/AffiliateSignup/AffiliateSignupModal";
import { useDisclosure } from "@components/Modal/useDisclosure";
import StepCard from "@components/StepCard";
import schema from "@components/faqSchema/affiliate-partner-program-faqSchema.json";
import ClientLogos from "@components/new-ui/ClientLogos";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import { Product } from "@components/new-ui/IndustriesSection/ProductSection";
import NewLayout from "@components/new-ui/Layout";
import PartnerProgramOverview, { ProgramOverview } from "@components/new-ui/partner-program/PartnerProgramOverview";
import YellowCTAhref from "@components/yellowCTAhref";
import Head from "next/head";
import React from "react";

const herosectionData: HerosectionProps = {
    chipTitle: "AFFILIATE PROGRAM",
    title: "Turn your network into income — earn commissions with Gallabox",
    description: "Join our free affiliate program and earn recurring rewards for every customer you bring. No fees, no fuss — just bigger payouts.",
    primaryButtonText: "Start earning now ",
    imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/affiliate-partner-program-hero.png",
};

const partnerCardData: Array<Product> = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/commission.png",
        title: "Recurring commission",
        description: "Earn on every new customer’s first purchase and all future upgrades.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/cookie-period.png",
        title: "90-day cookie period",
        description: "Get credited for signups within 90 days of your link being clicked.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/plug-and-play.png",
        title: "Plug-and-play creatives",
        description: "Access ready-to-use banners, images, and copy from our resource hub.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/track-click.png",
        title: "Unified dashboard",
        description: "Track clicks, signups, earnings, and payouts in one place.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/support-team.png",
        title: "Dedicated support team",
        description: "Get full assistance from onboarding to payouts.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/upgrade.png",
        title: "Ongoing upgrade earnings",
        description: "Continue earning as your referrals scale and upgrade.",
    },
];

const featureInfo1: FeatureInfo = {
    mainTitle: "Who’s it meant for?",
    imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/team.png",
    featureInfoList: ["Web developers", "Digital marketing agencies", "Content creators", "Happy customers or Gallabox users", "Tech reviewers", "Influencers "],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Why partner with Gallabox? ",
    subtitle2:
        "Gallabox is a no-code, conversational platform that enables businesses to attract leads, engage with their customers, and close deals — all on WhatsApp. As a Gallabox affiliate, you’ll have the opportunity to earn attractive commissions while promoting the Gallabox platform, which is transforming the way businesses operate.",
    imageUrl: "https://assets.gallabox.com/gb-home/affiliate-partner/partner.png",
};

const partnerPageData = [
    {
        title: "Join the program",
        description: "Apply to the Gallabox Affiliate program by filling out a short form on our Tapfiliate page and get your unique referral link. ",
    },
    { title: "Refer Gallabox", description: "Use your unique affiliate link to promote Gallabox across your website, blog, social media, or email campaigns." },
    {
        title: "Earn commissions",
        description: "Earn a commission for every new business opportunity closed, and track progress in your Tapfiliate dashboard. The more you refer, the more you earn! ",
    },
];

const faqData: FAQSectionProps = {
    title: "FAQs",
    faqItems: [
        {
            question: "What is an affiliate program, and who is it for?",
            answer: "An affiliate program lets you earn commissions by promoting products or services. At Gallabox, it’s ideal for bloggers, influencers, or businesses looking to monetize their audience through referrals.",
        },
        {
            question: "What’s the difference between the affiliate and solutions partner programs?",
            answer: (
                <>
                    <li>
                        <span className="font-semibold">Affiliate program:</span> Earn commissions by sharing Gallabox via your content (blogs, social media, etc.). No client
                        management required.
                    </li>
                    <li>
                        <span className="font-semibold">Solutions partner program:</span> For agencies/consultants who directly implement Gallabox tools (like WhatsApp automation)
                        for clients.
                    </li>
                </>
            ),
        },
        {
            question: "What is the Gallabox affiliate program?",
            answer: "The Gallabox affiliate program lets you earn a commission for every business you refer that signs up or upgrades their plan. Affiliates promote our WhatsApp automated solutions and get paid for successful sales.",
        },
        {
            question: "Is there any cost to join the Gallabox affiliate program?",
            answer: "No. It’s free to join — there are no sign-up fees or hidden charges.",
        },
        {
            question: "How much commission do I earn from the Gallabox affiliate program?",
            answer: "We pay a commission on all plan sales and upgrades for two years from the date of referral.",
        },
        {
            question: "How are referrals tracked?",
            answer: "We use a 90-day cookie. If someone signs up within 90 days of clicking your link, you earn commission.",
        },
        {
            question: "Can I create a custom referral link?",
            answer: "Yes! Use Tapfiliate to generate custom or vanity links for tracking. Once approved, your unique link is available in your dashboard. Personalize links to match your audience and monitor performance seamlessly.",
        },

        {
            question: "When and how will I receive my commission payouts?",
            answer: "We pay commissions quarterly via PayPal or bank transfer. You can set your preferred method in Tapfiliate.",
        },
        {
            question: "What is the minimum payout threshold?",
            answer: "You need to accumulate at least ₹1,000 (or $10) before we issue a payout.",
        },
        {
            question: "What if a customer cancels their subscription?",
            answer: "Commissions for canceled/refunded plans are deducted from your earnings automatically.",
        },
        {
            question: "What happens if my referral doesn’t use my link?",
            answer: "Promote freely (links, banners, QR codes), but avoid spam or unethical platforms.",
        },
        {
            question: "What’s the cookie period?",
            answer: "90 days. Please check your Tapfiliate dashboard for the exact cookie period(It’s configured in the portal).",
        },
        {
            question: "Is there a performance bonus?",
            answer: "Not yet, but incentives like vouchers may launch soon.",
        },
        {
            question: "How do I track my performance?",
            answer: "Log in to your Tapfiliate dashboard to view clicks, sign-ups, and your commission balance in real time.",
        },
        {
            question: "What features should I highlight when promoting Gallabox’s WhatsApp broadcast and drip campaigns?",
            answer: (
                <>
                    <p>Focus on how Gallabox amplifies outreach with:</p>
                    <li>Contact segmentation for laser-focused messaging</li>
                    <li>Automated reply-to configuration to qualify leads and assign conversations</li>
                    <li> Real-time analytics dashboard for open rates and responses </li>
                    <li>Abandoned cart triggers to recover lost sales </li>
                    <li> Drip campaign scheduling to nurture prospects over time </li>
                    <li>Rich message templates (images, videos, multi-product catalogs)</li>
                    <li>Seamless tech-stack integrations for unified data sync</li>
                </>
            ),
        },
    ],
};

const PartnershipPage: React.FC = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    return (
        <NewLayout
            title="Join our exciting Affiliate Program and become a Partner"
            description="Gallabox's affiliate program promises great opportunities to unlock your earning potential. Easy to sign up, simple to earn. Apply now!"
            canonical="https://gallabox.com/affiliate-partner-program"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/affiliate-partner/partner-og.png" }}
            keywords="Gallabox Affiliate program, Gallabox partnership program, Gallabox affiliate marketing"
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Head>

            <Herosection
                herosection={herosectionData}
                customCTAButton={
                    <button
                        id="becomeanaffiliate"
                        onClick={onOpen}
                        className="px-4 py-2 text-white bg-blue-700 rounded-md hover:no-underline focus:outline-none hover:shadow-2xl text-base font-medium flex items-center w-fit cursor-pointer"
                    >
                        Start earning now
                    </button>
                }
            />
            <div className="lg:max-w-7xl mx-auto client-logos pt-2 md:pt-0 mb-20">
                <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                <ClientLogos />
            </div>

            <PartnerProgramOverview title="Gallabox affiliate perks" gridCols="md:grid-cols-3" partnerCardData={partnerCardData as ProgramOverview[]} />
            <div className="mt-20">
                <StepCard cards={partnerPageData} />
            </div>
            <FeatureInfoCard {...featureInfo1} />
            <FeatureInfoCard {...featureInfo2} />
            <section className="lg:mx-auto lg:my-10 mx-2 mt-0 mb-10">
                <YellowCTAhref
                    customText={
                        <>
                            <h2 className="text-2xl md:text-5xl font-bold relative inline-block text-white">Earn more rewards with Gallabox</h2>
                            <p className="text-2xl text-white mb-4">Become a part of our affiliate program and turn your influence into income!</p>
                        </>
                    }
                    buttonText="Start earning now"
                    cardStyles={{
                        cardColor: "radial-gradient(349.01% 109.52% at 50% 75.36%, #3364FA 0%, #1E3B94 58.22%, #000 100%)",
                        buttonTextColor: "text-white",
                        paddingBlock: "49px",
                    }}
                    onButtonClick={onOpen}
                    buttonVariant="primary-dark"
                    ctaTrackId="becomeanaffiliate"
                    buttonWithoutIcon
                />
            </section>
            <FAQSection faqData={faqData} />
            <AffiliateSignupModal isOpen={isOpen} onClose={onClose} />
        </NewLayout>
    );
};

export default PartnershipPage;
